/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

/*
 * FriendDiscoveryScreen — the last onboarding step before the app.
 *
 * Privacy-first by construction: the reassurance is shown UP FRONT, before any
 * permission prompt. When the user taps "Find my friends" we request contacts
 * permission, normalize numbers to E.164 in-memory, and match them against the
 * existing /phoneIndex engine (single-key point reads, viewer filtered out, no
 * enumeration, nothing persisted). The whole step is skippable.
 */

import { useCallback, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { Contacts } from "@capacitor-community/contacts";
import { Loader2, Lock, Sparkles, UserCheck, Users } from "lucide-react";
import * as Sentry from "@sentry/react";
import { auth, db } from "@/lib/firebase";
import { DEFAULT_COUNTRY } from "@/utils/phone";
import {
  matchContactsOnPhoneIndex,
  normalizeContacts,
  type MatchedContact,
  type RawContact,
} from "@/utils/contactMatch";
import OnboardingShell from "@/components/onboarding/OnboardingShell";

interface Props {
  onContinue: () => void;
  onBack?: () => void;
  step: number;
  totalSteps: number;
}

type Phase = "intro" | "matching" | "results" | "denied";

const PRIVACY_POINTS = [
  "Matched right on your device",
  "Numbers are hashed, never stored",
  "Discarded the moment we're done",
] as const;

export default function FriendDiscoveryScreen({
  onContinue,
  onBack,
  step,
  totalSteps,
}: Props) {
  const [phase, setPhase] = useState<Phase>("intro");
  const [matches, setMatches] = useState<MatchedContact[]>([]);

  const findFriends = useCallback(async () => {
    if (!Capacitor.isNativePlatform()) {
      // Contacts picking is native-only; nothing to match in the browser.
      setMatches([]);
      setPhase("results");
      return;
    }
    setPhase("matching");
    try {
      let perm = await Contacts.checkPermissions();
      if (perm.contacts !== "granted" && perm.contacts !== "limited") {
        perm = await Contacts.requestPermissions();
      }
      if (perm.contacts !== "granted" && perm.contacts !== "limited") {
        setPhase("denied");
        return;
      }

      const res = await Contacts.getContacts({
        projection: { name: true, phones: true },
      });
      const rows: RawContact[] = [];
      for (const c of res.contacts) {
        const phone = (c.phones ?? [])
          .map((p) => (p.number ?? "").trim())
          .find((n) => n.length > 0);
        if (!phone) continue;
        const name =
          c.name?.display?.trim() ||
          [c.name?.given, c.name?.family].filter(Boolean).join(" ").trim();
        if (!name) continue;
        rows.push({ contactId: c.contactId, displayName: name, rawPhone: phone });
      }

      const normalized = normalizeContacts(rows, DEFAULT_COUNTRY.region);
      const matched = await matchContactsOnPhoneIndex(
        db,
        normalized,
        auth.currentUser?.uid ?? null,
      );
      setMatches(matched);
      setPhase("results");
    } catch (err) {
      console.error("[FriendDiscoveryScreen] contact match failed:", err);
      Sentry.captureException(err, { tags: { area: "onboarding.discovery" } });
      // Non-fatal: drop to an empty results state so the user can still finish.
      setMatches([]);
      setPhase("results");
    }
  }, []);

  // ── intro ────────────────────────────────────────────────────────────────
  if (phase === "intro") {
    return (
      <OnboardingShell
        step={step}
        totalSteps={totalSteps}
        onBack={onBack}
        ariaLabel="Find your friends"
        ctaLabel="Find my friends"
        onCta={findFriends}
        secondaryLabel="Maybe later"
        onSecondary={onContinue}
      >
        <div className="mx-auto flex max-w-md flex-col items-center text-center">
          <div className="mt-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#7C52F5]/12 text-[#7C52F5] shadow-[0_12px_30px_-16px_rgba(124,82,245,0.7)]">
            <Users size={30} aria-hidden="true" />
          </div>
          <h1 className="mt-5 text-2xl font-bold tracking-tight text-[#2A2342]">
            Find your friends
          </h1>
          <p className="mt-2.5 text-[15px] leading-relaxed text-[#6B5FA0]">
            See which of your contacts are already on MyAmbits — without giving
            up your privacy.
          </p>

          <div className="mt-8 w-full rounded-2xl border border-[#E0D8F3] bg-white p-5 text-left shadow-[0_10px_30px_-18px_rgba(124,82,245,0.6)]">
            <div className="flex items-center gap-2 text-[#7C52F5]">
              <Lock size={16} aria-hidden="true" />
              <span className="text-sm font-bold">Your contacts stay yours</span>
            </div>
            <ul className="mt-3 flex flex-col gap-2.5">
              {PRIVACY_POINTS.map((p) => (
                <li key={p} className="flex items-start gap-2.5 text-sm text-[#4A4068]">
                  <UserCheck
                    size={16}
                    className="mt-0.5 shrink-0 text-[#7C52F5]"
                    aria-hidden="true"
                  />
                  {p}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </OnboardingShell>
    );
  }

  // ── matching ───────────────────────────────────────────────────────────────
  if (phase === "matching") {
    return (
      <OnboardingShell step={step} totalSteps={totalSteps} ariaLabel="Finding friends">
        <div className="mx-auto flex max-w-md flex-col items-center pt-24 text-center">
          <Loader2 className="h-9 w-9 animate-spin text-[#7C52F5]" aria-hidden="true" />
          <p className="mt-5 text-base font-semibold text-[#2A2342]">
            Looking for your people…
          </p>
          <p className="mt-1.5 text-sm text-[#6B5FA0]">
            Matching on your device — this stays private.
          </p>
        </div>
      </OnboardingShell>
    );
  }

  // ── denied ─────────────────────────────────────────────────────────────────
  if (phase === "denied") {
    return (
      <OnboardingShell
        step={step}
        totalSteps={totalSteps}
        onBack={() => setPhase("intro")}
        ariaLabel="Contacts permission needed"
        ctaLabel="Continue"
        onCta={onContinue}
        secondaryLabel="Try again"
        onSecondary={() => setPhase("intro")}
      >
        <div className="mx-auto flex max-w-md flex-col items-center pt-12 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[#7C52F5]/12 text-[#7C52F5]">
            <Lock size={30} aria-hidden="true" />
          </div>
          <h1 className="mt-5 text-2xl font-bold tracking-tight text-[#2A2342]">
            No worries
          </h1>
          <p className="mt-2.5 text-[15px] leading-relaxed text-[#6B5FA0]">
            We don't have access to your contacts — that's completely fine. You
            can always add friends by number later from the Friends tab.
          </p>
        </div>
      </OnboardingShell>
    );
  }

  // ── results ────────────────────────────────────────────────────────────────
  const hasMatches = matches.length > 0;
  return (
    <OnboardingShell
      step={step}
      totalSteps={totalSteps}
      ariaLabel="Friends found"
      ctaLabel={hasMatches ? "Continue" : "Done"}
      onCta={onContinue}
    >
      <div className="mx-auto flex max-w-md flex-col">
        <div className="flex flex-col items-center text-center">
          <div className="mt-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#7C52F5]/12 text-[#7C52F5]">
            {hasMatches ? (
              <Sparkles size={30} aria-hidden="true" />
            ) : (
              <Users size={30} aria-hidden="true" />
            )}
          </div>
          <h1 className="mt-5 text-2xl font-bold tracking-tight text-[#2A2342]">
            {hasMatches
              ? `${matches.length} ${matches.length === 1 ? "friend" : "friends"} already here`
              : "No matches yet"}
          </h1>
          <p className="mt-2.5 text-[15px] leading-relaxed text-[#6B5FA0]">
            {hasMatches
              ? "Add them from the Friends tab whenever you're ready."
              : "None of your contacts are on MyAmbits yet — invite them from the Friends tab anytime."}
          </p>
        </div>

        {hasMatches && (
          <ul className="mt-7 flex flex-col gap-3">
            {matches.map((m) => (
              <li
                key={m.contactId}
                className="flex items-center gap-3 rounded-2xl border border-[#E0D8F3] bg-white px-4 py-3.5 shadow-[0_8px_24px_-16px_rgba(124,82,245,0.5)]"
              >
                <span
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#7C52F5]/12 text-base font-bold text-[#7C52F5]"
                  aria-hidden="true"
                >
                  {m.initial}
                </span>
                <p className="min-w-0 flex-1 truncate font-semibold text-[#2A2342]">
                  {m.displayName}
                </p>
                <span className="shrink-0 rounded-full bg-[#EDE7FB] px-3 py-1 text-xs font-semibold text-[#7C52F5]">
                  On MyAmbits
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </OnboardingShell>
  );
}
